import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  /** Helper / hint shown below the input. */
  helperText?: ReactNode;
  /** Error text. Overrides helperText when present and sets aria-invalid. */
  errorText?: ReactNode;
  /** Show a small required asterisk after the label. */
  required?: boolean;
  /** Icon rendered inside the input on the left. */
  leftIcon?: ReactNode;
  /** Icon or action rendered inside the input on the right. */
  rightSlot?: ReactNode;
  /** Block input — takes full width of its container. */
  fullWidth?: boolean;
}

/**
 * The single text-input primitive. Composes with leftIcon (e.g. magnifier
 * for search inputs) and rightSlot (e.g. clear button, unit suffix).
 *
 * For multi-line, use TextArea. For select-like, use Select.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    errorText,
    required,
    leftIcon,
    rightSlot,
    fullWidth,
    id,
    className,
    type = 'text',
    ...rest
  },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `input-${reactId}`;
  const helperId = helperText || errorText ? `${inputId}-helper` : undefined;
  const hasError = Boolean(errorText);

  return (
    <div className={clsx(styles.field, fullWidth && styles.fullWidth, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </label>
      )}
      <div className={clsx(styles.inputWrap, hasError && styles.hasError)}>
        {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          type={type}
          aria-invalid={hasError || undefined}
          aria-describedby={helperId}
          required={required}
          className={styles.input}
          {...rest}
        />
        {rightSlot && <span className={styles.rightSlot}>{rightSlot}</span>}
      </div>
      {(errorText || helperText) && (
        <p id={helperId} className={clsx(styles.helper, hasError && styles.errorText)}>
          {errorText ?? helperText}
        </p>
      )}
    </div>
  );
});
